import { useState, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { loadModules } from 'esri-loader';
import styled from '@emotion/styled/macro';
import { Tabs, TabList, TabPanels, Tab, TabPanel, Box, Button, Flex, Link, Spinner, Text } from '@chakra-ui/react';

import Combobox from '../forms/Combobox';

import MapTreeNode from './MapTreeNode';
import MapFilter from './MapFilter';
import { isoConfig } from '../../services/portfolios/isoConfig';
import { layersConfig } from '../../services/portfolios/layersConfig';

const allPowerplants = [
    'Other Power Plants - USA (EIA)',
    'Wind-based Power Plants - USA (EIA)',
    'Solar-based Power Plants - USA (EIA)',
    'Pumped-storage Power Plants - USA (EIA)',
    'Petroleum-based Power Plants - USA (EIA)',
    'Nuclear Power Plants - USA (EIA)',
    'Natural Gas Power Plants - USA (EIA)',
    'Hydorelectric Power Plants - USA (EIA)',
    'Geothermal Power Plants - USA (EIA)',
    'Coal-based Power Plants - USA (EIA)',
    'Biomass Plants - USA (EIA)',
    'Battery Storage Plants - USA (EIA)',
];

const allPlannedStorage = [
    'Planned Storage & Solar Projects (Construction)',
    'Planned Storage & Solar Projects (Contracted)',
    'Planned Storage & Solar Projects (Operational)',
    'Planned Storage & Solar Projects (Pipeline/Proposed)',
];

const allISO = ['SPP', 'PJM', 'NYISO', 'MISO', 'ISO-NE', 'ERCOT', 'CAISO w/ EIM', 'CAISO'];

const organizeLayers = (layers, view) => {
    let organizedLayers = [];
    let powerplants = [];
    let iso = [];
    let plannedStorage = [];

    for (const layer of layers) {
        if (layer.title) {
            const layerInfo = layersConfig.find((elem) => elem.title === layer.title);
            layer.description = layerInfo ? layerInfo.description : '';
            layer.custom_title = layerInfo && layerInfo.custom_title ? layerInfo.custom_title : null;
        }

        layer.visibleAtScale = view.scale < layer.minScale || layer.minScale === 0;
        // Add in a different array all powerplants so we can later on add them to the fake node as children nodes.
        if (layer.title && allPowerplants.includes(layer.title)) {
            powerplants.push(layer);
            continue;
        }

        if (layer.title && allISO.includes(layer.title)) {
            iso.push(layer);
            continue;
        }

        if (layer.title && allPlannedStorage.includes(layer.title)) {
            plannedStorage.push(layer);
            continue;
        }

        if (layer.fakeNode) continue;

        organizedLayers.push(layer);
    }

    // Add fake nodes to the organizedLayers array
    const nodePowerplant = layers.find((elem) => elem.id === 'powerplant_node');
    nodePowerplant.childrenNodes = powerplants;
    organizedLayers.push(nodePowerplant);

    const isoLayer = layers.find((elem) => elem.id === 'iso_node');
    isoLayer.childrenNodes = iso;
    organizedLayers.unshift(isoLayer);

    const plannedStorageLayer = layers.find((elem) => elem.id === 'planned_storage_node');
    plannedStorageLayer.childrenNodes = plannedStorage;
    organizedLayers.unshift(plannedStorageLayer);

    return organizedLayers.sort(function (a, b) {
        return a.layerOrder - b.layerOrder;
    });
};

const MapDrawer = ({ layers, map, view }) => {
    const [allLayers, setAllLayers] = useState(organizeLayers(layers, view));
    const [selected, setSelected] = useState();
    const [filters, setFilters] = useState([]);
    const [filterResult, setFilterResult] = useState();
    const [currentIsoConfig, setIsoConfig] = useState();
    const [study, setStudy] = useState();
    const [year, setYear] = useState();
    const [currentTab, setTab] = useState(0);
    const [histogramLoading, setHistogramLoading] = useState(false);
    const [filterLoading, setFilterLoading] = useState(false);

    const filtersInfo = useRef([]);

    const changeExtent = async (layer) => {
        const extent = await layer.queryExtent();
        view.goTo(extent);
    };

    const onStudyChange = (studyName) => {
        const study = currentIsoConfig.studies.find((elem) => elem.name === studyName);
        setStudy(study);
        setYear(study.years[0]);

        histogramColorRenderer(study.years[0].fieldName);
    };

    const onYearChange = (fieldName) => {
        const newYear = study.years.find((elem) => elem.fieldName === fieldName);
        setYear(newYear);

        histogramColorRenderer(fieldName);
    };

    const histogramColorRenderer = async (fieldName, layer) => {
        setHistogramLoading(true);
        const [colorSchemes, colorRendererCreator] = await loadModules([
            'esri/smartMapping/symbology/color',
            'esri/smartMapping/renderers/color',
        ]);

        const colorParams = {
            layer: layer ? layer : selected,
            valueExpression: `$feature.${fieldName}`,
            view: view,
            colorScheme: colorSchemes.flipColors(
                colorSchemes.getSchemeByName({
                    name: 'Blue and Red 9',
                    geometryType: 'point',
                    theme: 'above-and-below',
                })
            ),

            defaultSymbolEnabled: false,
        };

        let result = await colorRendererCreator.createContinuousRenderer(colorParams);
        setHistogramLoading(false);
        if (layer) {
            layer.renderer = result.renderer;
        } else {
            selected.renderer = result.renderer;
        }
    };

    const hideOtherLayers = (layerTitle) => {
        // Sets all layers to opacity 0%, except the selected the layer
        for (let layer of allLayers) {
            layer.effect = '';
            if (layer.title !== layerTitle) {
                layer.effect = 'opacity(0%)';

                if (layer.childrenNodes && layer.childrenNodes.length > 0) {
                    for (let sublayer of layer.childrenNodes) {
                        sublayer.effect = '';
                        if (sublayer.title !== layerTitle) {
                            sublayer.effect = 'opacity(0%)';
                        }
                    }
                }
            }
        }
    };

    const changeIsoHandler = (isoLayerName) => {
        if (isoLayerName) {
            //Clear filters, results, and highlights
            clearFilters();

            //Find the selected iso layer
            const iso_layer = allLayers.find((elem) => elem.id === 'iso_node');
            const selectedLayer = iso_layer.childrenNodes.find((elem) => elem.title === isoLayerName);
            setSelected(selectedLayer);

            //Get the new ISO layer config values
            const layer_config = isoConfig.find((elem) => elem.name === isoLayerName);
            setIsoConfig(layer_config);

            const study = layer_config.studies[0];
            setStudy(study);
            setYear(study.years[0]);

            // Create color renderer and assign to current layer
            if (currentTab === 0) {
                histogramColorRenderer(study.years[0].fieldName, selectedLayer);
            }

            // Move map to layer extent
            changeExtent(selectedLayer);

            // Show only selected layer
            hideOtherLayers(isoLayerName);
        }
    };

    useEffect(() => {
        const eventHandler = view.watch('scale', () => {
            let newLayers = organizeLayers(layers, view);
            setAllLayers([...newLayers]);
        });

        return function cleanup() {
            eventHandler.remove();
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setAllLayers(organizeLayers(layers, view));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layers]);

    const addNewFilter = () => {
        let newFilter = {
            field: '',
            operator: '',
            input: '',
        };
        setFilters([...filters, newFilter]);
        filtersInfo.current = [...filtersInfo.current, newFilter];
    };

    const onFilterChange = (value, index) => {
        let tempArr = [...filtersInfo.current];
        tempArr[index] = value;
        filtersInfo.current = tempArr;
    };

    const onTabChange = (index) => {
        setTab(index);
        clearFilters();

        // on Layers tab select, show all layers once again by removing their opacity
        if (index === 1) {
            for (let layer of allLayers) {
                layer.effect = '';

                if (!layer.childrenNodes) continue;
                for (let sublayer of layer.childrenNodes) {
                    sublayer.effect = '';
                }
            }
        }

        // on Filters tab select, show only the selected layer if defined
        if (index === 2) {
            if (selected) {
                changeExtent(selected);
                hideOtherLayers(selected.title);
            }
        }
    };

    const runFilters = async () => {
        if (!selected) {
            return;
        }

        setFilterLoading(true);
        let whereQuery = '';

        // Generate Query
        // @TODO: Check if esri provides helper methods/classes to generate queries
        for (let [index, filter] of filtersInfo.current.entries()) {
            filter.input = filter.input.replace(',', '.');

            if (filter.input === '') continue;

            whereQuery += filter.field + ' ' + filter.operator + ' ' + filter.input;

            if (filtersInfo.current.length !== index + 1) {
                whereQuery += ' AND ';
            }
        }

        let query = selected.createQuery();
        query.where = whereQuery;
        query.returnGeometry = true;
        let features = await selected.queryFeatures(query);
        setFilterResult(features);
        setFilterLoading(false);
    };

    const highlightAndZoom = async () => {
        const [Graphic] = await loadModules(['esri/Graphic']);
        let graphics = [];
        filterResult.features.forEach((g) => {
            let graphic = new Graphic({
                geometry: g.geometry,
                symbol: {
                    type: 'simple-marker',
                    style: 'circle',
                    size: 8,
                    color: [0, 0, 0, 0],
                    outline: {
                        color: [0, 255, 255, 0.5],
                        width: 2,
                    },
                },
                attributes: g.attributes,
            });
            graphics.push(graphic);
        });
        view.graphics.removeAll();
        view.graphics.addMany(graphics);

        view.goTo(graphics);
    };

    const clearFilters = () => {
        setFilters([]);
        filtersInfo.current = [];
        view.graphics.removeAll();
        setFilterResult();
    };

    return (
        <>
            <Tabs onChange={onTabChange}>
                <CustomTabList>
                    <CustomTab>
                        <FormattedMessage id="map_drawer_tab_histogram" />
                    </CustomTab>
                    <CustomTab>
                        <FormattedMessage id="map_drawer_tab_layers" />
                    </CustomTab>
                    <CustomTab>
                        <FormattedMessage id="map_drawer_tab_filters" />
                    </CustomTab>
                </CustomTabList>

                <TabPanels>
                    <TabPanel>
                        <Combobox
                            options={allISO}
                            placeholderLabel="Select ISO layer"
                            onChange={changeIsoHandler}
                            value={selected?.title}
                        />

                        {selected && (
                            <>
                                <Combobox
                                    my={3}
                                    options={currentIsoConfig.studies}
                                    valueKey="name"
                                    labelKey="name"
                                    onChange={onStudyChange}
                                    value={study.name}
                                />
                                <Combobox
                                    my={3}
                                    options={study.years}
                                    valueKey="fieldName"
                                    labelKey="year"
                                    value={year.fieldName}
                                    onChange={onYearChange}
                                />
                            </>
                        )}

                        {histogramLoading && (
                            <Flex my={5} direction="column" align="center">
                                <Spinner my={3} />
                                <Text>
                                    <FormattedMessage id="common_loading" />
                                </Text>
                            </Flex>
                        )}
                    </TabPanel>

                    <TabPanel>
                        {allLayers.map((elem) => (
                            <MapTreeNode key={elem.id} layer={elem} map={map} />
                        ))}
                    </TabPanel>

                    <TabPanel>
                        <Combobox
                            options={allISO}
                            placeholderLabel="Select ISO layer"
                            onChange={changeIsoHandler}
                            value={selected?.title}
                        />

                        {selected && (
                            <Flex my={3} justify="flex-end">
                                <Link onClick={addNewFilter}>
                                    <FormattedMessage id="map_drawer_new_filter" />
                                </Link>
                            </Flex>
                        )}

                        {filters.map((elem, index) => {
                            return (
                                <MapFilter
                                    key={index}
                                    filter={elem}
                                    onFilterChange={(val) => onFilterChange(val, index)}
                                    layerSelected={selected.title}
                                    isoConfig={currentIsoConfig}
                                />
                            );
                        })}

                        <Flex mt={3} justify="center" align="center">
                            <Button disabled={filterLoading} mx={3} variant="primary" onClick={runFilters}>
                                <Box as="span" textTransform="capitalize">
                                    <FormattedMessage id="map_drawer_run_filter" />
                                </Box>
                            </Button>
                            <Button mx={3} variant="secondary" onClick={clearFilters}>
                                <Box as="span" textTransform="capitalize">
                                    <FormattedMessage id="map_drawer_clear_filter" />
                                </Box>
                            </Button>
                        </Flex>

                        {filterResult && (
                            <Flex mt={3} align="center" direction="column">
                                <p style={{ width: 'fit-content' }}>{filterResult.features.length} matching nodes</p>

                                <Button my={3} variant="special-green" onClick={highlightAndZoom}>
                                    <Box as="span" textTransform="capitalize">
                                        <FormattedMessage id="map_drawer_highlight_and_zoom" />
                                    </Box>
                                </Button>
                            </Flex>
                        )}
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </>
    );
};

const CustomTabList = styled(TabList)`
    overflow-x: auto;
    padding: 5px;
    border-bottom: 1px solid var(--chakra-colors-gray-200);

    [aria-selected='true'] {
        border-bottom: 4px solid var(--chakra-colors-blue-400);
    }
`;

const CustomTab = styled(Tab)`
    padding: 16px 0;
    margin-right: 24px;
    margin-bottom: 0;

    :focus {
        border-radius: var(--chakra-radii-base);
    }
`;

export default MapDrawer;
